import { useNavigate } from 'react-router-dom';
import { ArrowRight, Zap, Globe, Thermometer } from 'lucide-react';
import SectionReveal from './SectionReveal';

const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/ed16aff2-3768-4294-8b8e-fb676e7a6422/images/f206875e13a7f404172e6419d59210384695a3f3426c4acb4bc933e7a6936578.png';

const PILLARS = [
  { Icon: Globe,        label: 'Deploy Anywhere',     ro: 'Implementare Oriunde' },
  { Icon: Thermometer,  label: 'Extreme Environments', ro: 'Medii Extreme' },
  { Icon: Zap,          label: 'Instant Power 24/7',   ro: 'Energie Instantanee 24/7' },
];

function TeaserBackground() {
  return (
    <div className="absolute right-0 top-0 bottom-0 w-1/2 lg:w-[48%] overflow-hidden">
      <img src={HERO_IMG} alt="Holy Grail Unit" className="w-full h-full object-cover opacity-35" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/60 to-transparent" />
    </div>
  );
}

function TeaserPillars({ isRO }) {
  return (
    <div className="flex flex-wrap gap-3 mb-10">
      {PILLARS.map(({ Icon, label, ro }) => (
        <div key={label} className="flex items-center gap-2 px-3 py-2 border border-white/10 bg-white/[0.03]">
          <Icon size={13} color="#D4A017" />
          <span className="text-xs text-white/60 tracking-wide">{isRO ? ro : label}</span>
        </div>
      ))}
    </div>
  );
}

function TeaserStats({ isRO }) {
  return (
    <div className="flex gap-8 mb-10 border-l-2 border-[#D4A017] pl-6">
      <div>
        <div className="text-2xl font-semibold text-[#D4A017]" style={{ fontFamily: "'Outfit', sans-serif" }}>€0.063/kWh</div>
        <div className="text-xs text-white/35 uppercase tracking-widest mt-0.5">{isRO ? 'Tarif Curent' : 'Current Rate'}</div>
      </div>
      <div className="w-px bg-white/10" />
      <div>
        <div className="text-2xl font-semibold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>0</div>
        <div className="text-xs text-white/35 uppercase tracking-widest mt-0.5">CO₂ {isRO ? 'Emisii' : 'Emissions'}</div>
      </div>
      <div className="w-px bg-white/10" />
      <div>
        <div className="text-2xl font-semibold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>24/7</div>
        <div className="text-xs text-white/35 uppercase tracking-widest mt-0.5">{isRO ? 'Disponibilitate' : 'Availability'}</div>
      </div>
    </div>
  );
}

export default function HolyGrailTeaser({ lang }) {
  const navigate = useNavigate();
  const isRO = lang === 'RO';

  return (
    <section className="relative py-0 bg-[#050505] overflow-hidden">
      <div className="h-px bg-gradient-to-r from-transparent via-[#D4A017]/40 to-transparent" />
      <div className="relative">
        <TeaserBackground />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} />
        <div className="relative z-10 container mx-auto px-6 lg:px-12 max-w-7xl py-20 sm:py-28">
          <div className="max-w-2xl">
            <SectionReveal>
              <p className="text-xs tracking-[0.32em] uppercase text-[#D4A017] mb-5 font-medium">
                {isRO ? 'KÖBER Energy Holding — Tehnologie de Avangardă' : 'KÖBER Energy Holding — Breakthrough Technology'}
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-tight mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {isRO ? <><span className="text-[#D4A017]">Sfântul Graal</span><br />al Energiei Regenerabile</> : <>The <span className="text-[#D4A017]">Holy Grail</span><br />of Renewable Energy</>}
              </h2>
              <p className="text-base text-white/55 leading-relaxed mb-8 max-w-xl">
                {isRO
                  ? 'O unitate independentă de generare a energiei: energie nelimitată, continuă, curată — disponibilă 24/7, zero CO₂, implementabilă oriunde.'
                  : 'An independent power generation unit delivering limitless, continuous, clean energy — available 24/7, zero CO₂, deployable anywhere on Earth.'}
              </p>
              <TeaserPillars isRO={isRO} />
              <TeaserStats isRO={isRO} />
              <button onClick={() => navigate('/holy-grail')} data-testid="holy-grail-teaser-cta"
                className="group inline-flex items-center gap-3 bg-[#D4A017] hover:bg-[#F5B814] text-black font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-200"
                style={{ fontFamily: "'Outfit', sans-serif" }}>
                {isRO ? 'Descoperă Tehnologia' : 'Discover the Technology'}
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-200" />
              </button>
            </SectionReveal>
          </div>
        </div>
      </div>
      <div className="h-px bg-gradient-to-r from-transparent via-[#D4A017]/40 to-transparent" />
    </section>
  );
}
